import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { SectionHeading } from "../components/Section";
import ProductCard from "../components/ProductCard";
import { useApp } from "../context/AppContext";
import { useReveal } from "../hooks/useReveal";
import Seo from "../components/Seo";
import { FaqSection } from "../components/FaqSection";
import { AtAGlance } from "../components/AtAGlance";
import { faqData } from "../data/faqData";
import { IMAGES, products as mockProducts, testimonials } from "../data/mock";
import { getProducts } from "../services/api";
import { Wine, Beaker, PenTool, Truck, ArrowRight, Quote, Grape } from "lucide-react";

const Home = () => {
  const { t, lang } = useApp();
  const [products, setProducts] = useState(mockProducts);
  useReveal([products]);

  useEffect(() => {
    getProducts().then((d) => { if (Array.isArray(d) && d.length) setProducts(d); }).catch(() => {});
  }, []);

  const grapeWines = products.filter((p) => p.category === "single");
  const starterSets = products.filter((p) => p.category === "starter").slice(0, 4);

  const concepts = [
    { title: t("home.single"), desc: t("home.singleDesc"), img: IMAGES.bottles3, to: "/store" },
    { title: t("home.starter"), desc: t("home.starterDesc"), img: IMAGES.cellar1, to: "/store" },
    { title: t("home.bestseller"), desc: t("home.bestsellerDesc"), img: IMAGES.bottles1, to: "/wine-configurator" },
  ];

  const steps = [
    { icon: Beaker, title: t("home.step1"), desc: t("home.step1d") },
    { icon: Wine, title: t("home.step2"), desc: t("home.step2d") },
    { icon: PenTool, title: t("home.step3"), desc: t("home.step3d") },
    { icon: Truck, title: t("home.step4"), desc: t("home.step4d") },
  ];

  return (
    <div>
      <Seo title={t("home.heroTitle")} description={t("home.heroSub")} image={IMAGES.heroPour} path="/" lang={lang} />
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] flex items-center justify-center overflow-hidden">
        <video
          data-testid="hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/hero_poster.jpg"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/hero.webm" type="video/webm" />
          <source src="/hero.mp4" type="video/mp4" />
          <img src={IMAGES.heroPour} alt="Rotwein wird in ein Weinglas eingeschenkt" className="absolute inset-0 w-full h-full object-cover" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-ink/65 via-ink/45 to-ink/80" />
        <div className="relative z-10 text-center px-6 max-w-4xl">
          <span className="inline-block text-gold text-sm font-semibold uppercase tracking-[0.35em] mb-5 animate-fade-up">{t("home.heroEyebrow")}</span>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-cream leading-[1.05] text-shadow-lg animate-fade-up" style={{ animationDelay: "0.1s" }}>
            {t("home.heroTitle")}
          </h1>
          <p className="mt-6 text-cream/85 text-lg md:text-xl max-w-2xl mx-auto animate-fade-up" style={{ animationDelay: "0.2s" }}>
            {t("home.heroSub")}
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4 animate-fade-up" style={{ animationDelay: "0.3s" }}>
            <Link to="/wine-configurator">
              <Button className="bg-wine hover:bg-wine-dark text-white h-14 px-9 text-base rounded-full gap-2 shadow-lg">
                {t("common.blendNow")} <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/how-it-works">
              <Button variant="outline" className="h-14 px-9 text-base rounded-full bg-cream/10 border-cream/40 text-cream hover:bg-cream hover:text-ink backdrop-blur-sm">
                {t("common.learnMore")}
              </Button>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-cream/60 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-cream/40 flex items-start justify-center p-1.5">
            <div className="w-1 h-2 rounded-full bg-gold" />
          </div>
        </div>
      </section>

      {/* AT A GLANCE (answer-first) */}
      <AtAGlance lang={lang} />

      {/* CONCEPT */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow={t("home.conceptTag")} title={t("home.conceptTitle")} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14">
            {concepts.map((c, i) => (
              <div key={i} className="reveal group bg-white rounded-2xl overflow-hidden border border-border hover:shadow-2xl transition-all duration-500" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="h-52 overflow-hidden">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                </div>
                <div className="p-7">
                  <h3 className="font-serif text-2xl text-wine">{c.title}</h3>
                  <p className="mt-3 text-ink/65 leading-relaxed">{c.desc}</p>
                  <Link to={c.to} className="inline-flex items-center gap-1.5 mt-5 text-wine font-medium text-sm group/link">
                    {t("common.buyNow")} <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="py-24 bg-ink relative overflow-hidden">
        <img src={IMAGES.cellar2} alt="Weinkeller mit Eichenfässern" className="absolute inset-0 w-full h-full object-cover opacity-10" />
        <div className="relative max-w-7xl mx-auto px-6">
          <SectionHeading light title={t("home.stepsTitle")} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-16">
            {steps.map((s, i) => (
              <div key={i} className="reveal text-center" style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="relative mx-auto w-20 h-20 rounded-full bg-wine flex items-center justify-center mb-6 border border-gold/40">
                  <s.icon className="w-9 h-9 text-gold" />
                  <span className="absolute -top-2 -right-1 w-7 h-7 rounded-full bg-gold text-ink text-sm font-bold flex items-center justify-center">{i + 1}</span>
                </div>
                <h3 className="font-serif text-xl text-cream">{s.title}</h3>
                <p className="mt-2 text-cream/60 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GRAPE VARIETY WINES */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow={t("home.grapeSub")} title={t("home.grapeTitle")} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7 mt-14">
            {grapeWines.map((p) => <div key={p.id} className="reveal"><ProductCard product={p} /></div>)}
          </div>
          <div className="text-center mt-12 reveal">
            <Link to="/store"><Button variant="outline" className="rounded-full border-wine text-wine hover:bg-wine hover:text-white h-12 px-8">{t("common.seeAll")}</Button></Link>
          </div>
        </div>
      </section>

      {/* LABEL CTA */}
      <section className="py-24 bg-cream-dark">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal rounded-3xl overflow-hidden shadow-2xl">
            <img src={IMAGES.label1} alt="Custom wine label" className="w-full h-[480px] object-cover" />
          </div>
          <div className="reveal">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.25em] mb-3 text-wine">{t("home.labelTitle")}</span>
            <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight">{t("home.labelTitle")}</h2>
            <p className="mt-5 text-ink/65 text-lg leading-relaxed">{t("home.labelSub")}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/label-designer">
                <Button className="bg-wine hover:bg-wine-dark text-white h-13 px-8 rounded-full gap-2 h-12"><PenTool className="w-5 h-5" /> {t("home.labelCta")}</Button>
              </Link>
              <Link to="/store"><Button variant="outline" className="rounded-full border-ink/20 text-ink hover:bg-ink hover:text-cream h-12 px-8">{t("common.seeAll")}</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* STARTER SETS */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow={t("home.starterSelSub")} title={t("home.starterSelTitle")} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7 mt-14">
            {starterSets.map((p) => <div key={p.id} className="reveal"><ProductCard product={p} /></div>)}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-ink relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6">
          <SectionHeading light title={t("home.testimonialsTitle")} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14">
            {testimonials.map((tm, i) => (
              <div key={tm.id} className="reveal bg-wine/20 backdrop-blur border border-cream/10 rounded-2xl p-8" style={{ transitionDelay: `${i * 80}ms` }}>
                <Quote className="w-9 h-9 text-gold mb-4" />
                <p className="text-cream/85 leading-relaxed italic">"{tm.quote[lang]}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gold/20 flex items-center justify-center"><Grape className="w-5 h-5 text-gold" /></div>
                  <div>
                    <p className="font-serif text-cream text-lg leading-tight">{tm.name}</p>
                    <p className="text-cream/50 text-sm">{tm.role[lang]}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection faqs={faqData.home[lang] || faqData.home.de} lang={lang} />

      {/* FINAL CTA */}
      <section className="py-28 bg-cream relative overflow-hidden">
        <div className="max-w-3xl mx-auto px-6 text-center reveal">
          <Grape className="w-12 h-12 text-wine mx-auto mb-6" />
          <h2 className="font-serif text-4xl md:text-5xl text-ink leading-tight">{t("home.ctaTitle")}</h2>
          <p className="mt-5 text-ink/65 text-lg">{t("home.ctaSub")}</p>
          <Link to="/wine-configurator">
            <Button className="mt-9 bg-wine hover:bg-wine-dark text-white h-14 px-10 text-base rounded-full gap-2 shadow-lg">
              {t("common.blendNow")} <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
